import React from 'react';

import { StyledOcticon } from '@primer/react';
import { FileCodeIcon } from '@primer/octicons-react';

import { JsonEditor, OnErrorFunction, UpdateFunction, ThemeInput } from 'json-edit-react'

import { diff } from 'deep-object-diff';
import some from 'lodash/some';

import Label from '../Labels/Label';
import ErrorLabel from '../Labels/ErrorLabel';

const isUpdateRestricted = (update, restrictedProps) => {
  if (!restrictedProps?.length) {
    return false;
  }
  const path = update?.path?.length ? update.path : [];
  return some(path, (objKey) => some(restrictedProps, (restrictedProp) => restrictedProp === objKey));
}

const getAllNestedKeys = (obj, prefix?: string) =>
  Object.keys(obj).reduce((result: string[], objKey: string) => {
    if(Array.isArray(obj[objKey])) {
      return [...result, objKey];
    } else if(typeof obj[objKey] === 'object' && obj[objKey] !== null) {
      return [...result, ...getAllNestedKeys(obj[objKey], prefix + objKey + '.'), objKey];
    }
    return [...result, objKey];
  }, []);

const hasRestrictedPropertyChanged = (oldData, newData, restrictedProps) => {
  if (!restrictedProps?.length) {
    return false;
  }

  const comparison = diff(oldData || {}, newData || {});

  const nestedKeys = getAllNestedKeys(comparison);
  if (!nestedKeys?.length) {
    return false;
  }

  return some(nestedKeys, (objKey) => some(restrictedProps, (restrictedProp) => {
    const keyPath = objKey.split('.') || [];
    return some(keyPath, (keyPart) => restrictedProp === keyPart);
  }))
}

const NoJSONGraphic = () => (
  <StyledOcticon
    className="text-brand"
    icon={FileCodeIcon}
    size={32}
  />
)

type EditableJSONViewProps = {
  data: any,
  restrictedProps?: string[],
  setData?: (data: any) => void,
  onChange?: UpdateFunction,
  onError?: OnErrorFunction,
  showArrayIndices?: boolean,
  showStringQuotes?: boolean,
  showInternalErrorMessages?: boolean,
  enableEdit?: boolean,
  enableClipboard?: boolean,
  sortKeys?: boolean,
  truncateAt?: number,
  errorTimeout?: number,
  emptyMessage?: string,
  emptySubMessage?: string,
}

const DEFAULT_THEME: ThemeInput = [
  'default',
  {
    container: { backgroundColor: 'white' },
  }
];

export const NewEditableJSONView = ({
  data,
  restrictedProps = [],
  setData = () => {},
  onChange = () => {},
  onError = () => {},
  enableClipboard = true,
  enableEdit = false,
  showArrayIndices = true,
  showStringQuotes = true,
  showInternalErrorMessages = true,
  truncateAt = 250,
  errorTimeout = 4000,
  sortKeys = false,
  emptyMessage = 'No JSON detected',
  emptySubMessage = 'Click on the Edit button to get started!',
}: EditableJSONViewProps) => {

  const handleUpdate = (update) => {
    if (!enableEdit) {
      return 'Editing has been cancelled or is not currently enabled.'
    }
    if (isUpdateRestricted(update, restrictedProps) || hasRestrictedPropertyChanged(update.currentValue, update.newValue, restrictedProps)) {
      return 'Unable to add or modify a restricted field.';
    } else {
      onChange(update);
    }
  };

  const handleAddRestrictions = (update) => {
    return !enableEdit || isUpdateRestricted(update, restrictedProps);
  }

  const handleUpdateRestrictions = (update) => {
    const isTopLevel = !update?.level;
    const isAddOrDeleteOnly = (update.value && typeof update.value === 'object') || Array.isArray(update.value);
    return !enableEdit || isTopLevel || isAddOrDeleteOnly || isUpdateRestricted(update, restrictedProps);
  }

  const handleDeleteRestrictions = (update) => {
    const isTopLevel = !update?.level;
    return !enableEdit || isTopLevel || isUpdateRestricted(update, restrictedProps);
  }

  return (
    <div className={`px-4 w-full h-96 bg-white border border-border-dark rounded flex items-start justify-${data ? 'start' : 'center'} overflow-y-scroll`}>
      {
        data ? (
          <JsonEditor
            className="w-full"
            data={data}
            setData={setData}
            onUpdate={handleUpdate}
            onError={onError}
            enableClipboard={enableClipboard}
            restrictEdit={handleUpdateRestrictions}
            restrictDelete={handleDeleteRestrictions}
            restrictAdd={handleAddRestrictions}
            showArrayIndices={showArrayIndices}
            showStringQuotes={showStringQuotes}
            showErrorMessages={showInternalErrorMessages}
            showCollectionCount="when-closed"
            stringTruncate={truncateAt}
            errorMessageTimeout={errorTimeout}
            keySort={sortKeys}
            minWidth="full"
            maxWidth="none"
            theme={DEFAULT_THEME}
          />
        ) : (
          <div className="p-4 flex flex-col justify-center items-center h-full text-center">
            <NoJSONGraphic />
            <Label className="mt-2" size="lg">
              { emptyMessage }
            </Label>
            <ErrorLabel className="mt-2" size="md">
              { emptySubMessage }
            </ErrorLabel>
          </div>
        )
      }
    </div>
  )
}

export default NewEditableJSONView;
